/**
 * 🧲 HOOK MAGNÉTIQUE - Système d'Accroche Heartbeats
 * 
 * "Une grande promesse est l'âme de toute publicité" - Samuel Johnson
 * 
 * Track l'état de l'utilisateur et génère des messages magnétiques
 * pour le faire revenir et s'inscrire !
 */

import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'heartbeats_magnet';

// 🎯 États de l'utilisateur
export const USER_STATES = {
  NEVER_OPENED: 'never_opened',      // Jamais ouvert Heartbeats
  CURIOUS: 'curious',                 // A ouvert mais pas inscrit
  INCOMPLETE: 'incomplete',           // Profil commencé mais pas fini
  ACTIVE: 'active',                   // Profil complet et actif
  DORMANT: 'dormant'                  // Inscrit mais inactif > 7 jours
};

// 🔥 Messages magnétiques selon l'état
const MAGNETIC_MESSAGES = {
  // Jamais ouvert - créer la curiosité avec LA PROMESSE
  [USER_STATES.NEVER_OPENED]: {
    button: [
      { text: "💜 L'amour est au coin de la rue", subtext: "Découvrir" },
      { text: "💜 {count} célibataires près de toi", subtext: "Découvrir" },
      { text: "💜 Qui va te plaire ?", subtext: "Découvrir" }
    ],
    urgency: 'low'
  },
  
  // Curieux - il a goûté, on le ramène !
  [USER_STATES.CURIOUS]: {
    button: [
      { text: "🔥 Tu reviens ?", subtext: "On t'attendait..." },
      { text: "💘 Quelqu'un te cherche...", subtext: "Vérifier" },
      { text: "👀 {count} nouveaux profils", subtext: "Depuis ta dernière visite" },
      { text: "⚡ Des nouveautés t'attendent", subtext: "Voir" }
    ],
    urgency: 'high'
  },
  
  // Profil incomplet - le pousser à finir
  [USER_STATES.INCOMPLETE]: {
    button: [
      { text: "⚡ Plus qu'une étape !", subtext: "Finalise ton profil" },
      { text: "🔥 Ton profil est presque prêt", subtext: "Terminer" },
      { text: "🚀 {count} personnes attendent", subtext: "Finaliser" }
    ],
    urgency: 'high'
  },
  
  // Actif - encourager l'engagement
  [USER_STATES.ACTIVE]: {
    button: [
      { text: "💜 Heartbeats", subtext: null },
      { text: "🔔 {count} nouveautés", subtext: "Voir" },
      { text: "💌 {matches} matchs", subtext: "Répondre" }
    ],
    urgency: 'medium'
  },
  
  // Dormant - le réveiller
  [USER_STATES.DORMANT]: {
    button: [
      { text: "💔 Tu nous manques...", subtext: "Reviens !" },
      { text: "🌟 {count} personnes ont vu ton profil", subtext: "Découvrir qui" },
      { text: "🔥 Quelqu'un pense à toi...", subtext: "Vérifier" }
    ],
    urgency: 'high'
  }
};

// 🧚 Messages de PIXIE - Variés et avec PERSONNALITÉ !
// Elle est espiègle, drôle, un peu taquine mais toujours bienveillante
const PIXIE_MESSAGES = {
  curious_exit: [
    { title: "Hé toi !", message: "L'amour est peut-être à 2 rues d'ici... 🗺️" },
    { title: "Attends !", message: "J'ai pas fini de te montrer les pépites du coin ✨" },
    { title: "Déjà ?", message: "On commençait juste à s'amuser ! 🎉" },
    { title: "Psst...", message: "Quelqu'un de cool vient de se connecter 👀" },
    { title: "Oh non !", message: "Tu vas rater les potins du quartier 😏" },
    { title: "Hum...", message: "T'as même pas vu les nouveaux profils ! 🆕" },
    { title: "Sérieux ?", message: "Le canapé peut attendre 5 min non ? 🛋️" },
    { title: "Bon...", message: "Si tu pars, je mange tous les cookies 🍪" },
  ],
  incomplete_exit: [
    { title: "Presque !", message: "Ton profil est à {percent}%... si près du but ! 🎯" },
    { title: "Allez !", message: "Plus que quelques clics et c'est bon 🚀" },
    { title: "Courage !", message: "2 minutes et tu peux matcher 💜" },
    { title: "Hé !", message: "Ton futur crush attend de te découvrir ✨" },
  ],
  returning: [
    { title: "Re-coucou !", message: "T'as changé d'avis ? Je le savais 😎" },
    { title: "Ah ah !", message: "Tu pouvais pas résister hein ? 😏" },
    { title: "Yes !", message: "Je savais que tu reviendrais 🎉" },
    { title: "Cool !", message: "On reprend où on en était ? 💜" },
  ],
  active_exit: [
    { title: "À plus !", message: "Reviens vite, y'a du mouvement ici 👋" },
    { title: "Ciao !", message: "Je garde ton profil au chaud 🔥" },
    { title: "Bye !", message: "Tes matchs t'attendent, oublie pas 💌" },
    { title: "À bientôt !", message: "Je te notifie si y'a du nouveau 🔔" },
  ]
};

// Tracker les messages déjà montrés pour éviter répétition
let shownMessages = [];

// Sélectionner un message UNIQUE (pas de répétition)
const pickUniqueMessage = (category) => {
  const messages = PIXIE_MESSAGES[category] || PIXIE_MESSAGES.curious_exit;
  
  // Filtrer les messages déjà montrés
  const available = messages.filter(m => !shownMessages.includes(m.title));
  
  // Si tous montrés, reset et reprendre
  if (available.length === 0) {
    shownMessages = [];
    return messages[Math.floor(Math.random() * messages.length)];
  }
  
  const picked = available[Math.floor(Math.random() * available.length)];
  shownMessages.push(picked.title);
  
  // Garder max 5 en mémoire
  if (shownMessages.length > 5) shownMessages.shift();
  
  return picked;
};

// Pour compatibilité avec l'ancien format
export const RETENTION_POPUPS = {
  curious_exit: PIXIE_MESSAGES.curious_exit[0],
  incomplete_exit: PIXIE_MESSAGES.incomplete_exit[0],
  first_swipe_exit: PIXIE_MESSAGES.curious_exit[0]
};

// 🎰 Sélectionner un message aléatoire
const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)];

// 📊 Obtenir les stats du magasin (simulé pour l'instant, à connecter à l'API)
const getStoreStats = async () => {
  // TODO: Connecter à l'API réelle
  return {
    totalProfiles: Math.floor(Math.random() * 50) + 30,  // 30-80 profils
    newToday: Math.floor(Math.random() * 5) + 1,         // 1-5 nouveaux
    activeNow: Math.floor(Math.random() * 10) + 3        // 3-13 actifs
  };
};

/**
 * 🧲 Hook principal d'accroche magnétique
 */
export const useMagneticHook = (customerId, hasProfile = false, profileCompletion = 0) => {
  const [userState, setUserState] = useState(USER_STATES.NEVER_OPENED);
  const [magneticMessage, setMagneticMessage] = useState(null);
  const [stats, setStats] = useState({ totalProfiles: 0, newToday: 0, activeNow: 0 });
  const [showRetentionPopup, setShowRetentionPopup] = useState(false);
  const [retentionData, setRetentionData] = useState(null);

  // Charger l'état sauvegardé
  useEffect(() => {
    if (!customerId) return;
    
    const saved = localStorage.getItem(`${STORAGE_KEY}_${customerId}`);
    if (saved) {
      const data = JSON.parse(saved);
      
      // Déterminer l'état actuel
      if (hasProfile && profileCompletion >= 80) {
        // Vérifier si dormant
        const lastVisit = new Date(data.lastVisit || Date.now());
        const daysSince = (Date.now() - lastVisit) / (1000 * 60 * 60 * 24);
        setUserState(daysSince > 7 ? USER_STATES.DORMANT : USER_STATES.ACTIVE);
      } else if (hasProfile && profileCompletion < 80) {
        setUserState(USER_STATES.INCOMPLETE);
      } else if (data.hasOpened) {
        setUserState(USER_STATES.CURIOUS);
      }
    }
    
    // Charger les stats
    getStoreStats().then(setStats);
  }, [customerId, hasProfile, profileCompletion]);

  // Générer le message magnétique
  useEffect(() => {
    const messages = MAGNETIC_MESSAGES[userState];
    if (!messages) return;
    
    const template = pickRandom(messages.button);
    
    // Remplacer les variables
    let text = template.text
      .replace('{count}', stats.totalProfiles)
      .replace('{matches}', Math.floor(Math.random() * 3) + 1);
    
    setMagneticMessage({
      text,
      subtext: template.subtext,
      urgency: messages.urgency
    });
  }, [userState, stats]);

  // Tracker l'ouverture de Heartbeats
  const trackOpen = useCallback(() => {
    if (!customerId) return;
    
    const data = {
      hasOpened: true,
      lastVisit: new Date().toISOString(),
      visitCount: 1
    };
    
    const saved = localStorage.getItem(`${STORAGE_KEY}_${customerId}`);
    if (saved) {
      const prev = JSON.parse(saved);
      data.visitCount = (prev.visitCount || 0) + 1;
    }
    
    localStorage.setItem(`${STORAGE_KEY}_${customerId}`, JSON.stringify(data));
    
    // Mettre à jour l'état si premier passage
    if (!hasProfile && userState === USER_STATES.NEVER_OPENED) {
      setUserState(USER_STATES.CURIOUS);
    }
  }, [customerId, hasProfile, userState]);

  // Tracker la fermeture (pour popup de rétention)
  const trackClose = useCallback((reason = 'manual') => {
    if (!customerId) return false;
    
    // Afficher popup de rétention si curieux ou incomplet
    if (userState === USER_STATES.CURIOUS || userState === USER_STATES.INCOMPLETE) {
      const category = userState === USER_STATES.CURIOUS ? 'curious_exit' : 'incomplete_exit';
      const pixieMsg = pickUniqueMessage(category);
      
      setRetentionData({
        title: pixieMsg.title,
        message: pixieMsg.message.replace('{percent}', profileCompletion),
      });
      setShowRetentionPopup(true);
      return true; // Empêcher la fermeture immédiate
    }
    return false;
  }, [customerId, userState, profileCompletion]);

  // Confirmer la sortie
  const confirmExit = useCallback(() => {
    setShowRetentionPopup(false);
    setRetentionData(null);
  }, []);

  // Annuler la sortie
  const cancelExit = useCallback(() => {
    setShowRetentionPopup(false);
    setRetentionData(null);
  }, []);

  return {
    // État
    userState,
    magneticMessage,
    stats,
    
    // Popup de rétention
    showRetentionPopup,
    retentionData,
    
    // Actions
    trackOpen,
    trackClose,
    confirmExit,
    cancelExit,
    
    // Helpers
    isHighUrgency: magneticMessage?.urgency === 'high'
  };
};

export default useMagneticHook;
